import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, switchMap, throwError } from 'rxjs';

import {
  ChildSurveyFilter,
  GetSurveysParams,
  Survey,
  SurveyQuotaUpdate,
  SurveyStatusCount,
  SurveySupplierCpi,
} from '@ps/survey-utils';
import {
  HighCpiConfirmationActivityGenerator,
  SurveyStatus,
  type HighCpiConfirmationDetails,
} from '@ps/survey-kernel';
import { isTimeoutError, PSError } from '@ps/utils';

import { SURVEY_TIMEOUT_MESSAGE } from '../../utils';
import { QuotaPage, SurveyApi } from '../../api/survey.api';
import { QUOTAS_PAGE_LIMIT } from '../../state/survey-state.constants';
import {
  LaunchTrafficChannelRequestDto,
  SurveyPatchPayloadDto,
} from '../../api/dtos/survey.dto';
import { BillingRuleRecord } from '@ps/user-settings-models';

@Injectable({
  providedIn: 'root',
})
export class SurveyService {
  private readonly _api = inject(SurveyApi);

  public getAll(params: GetSurveysParams): Observable<Survey[]> {
    return this._api.getAll(params).pipe(
      catchError((error) => {
        if (isTimeoutError(error)) {
          return throwError(() =>
            PSError.from(SURVEY_TIMEOUT_MESSAGE, 'survey-load-timeout', {
              kind: 'timeout',
              notify: true,
            }),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  public getCounts(params: GetSurveysParams): Observable<SurveyStatusCount> {
    return this._api.getCounts(params);
  }

  public getChildSurveys(
    parentId: number,
    filter: ChildSurveyFilter,
  ): Observable<Survey[]> {
    return this._api.getChildSurveys(filter).pipe(
      // The grouped/child survey endpoint can return the parent row in the same list.
      // The expanded row is already shown above; drop it so the child list is only descendants.
      map((surveys) => surveys.filter((s) => s.id !== parentId)),
    );
  }

  public getTrafficChannelChildren(surveyId: number): Observable<Survey[]> {
    return this._api.getTrafficChannelChildren(surveyId);
  }

  public updateSurveyStatus(
    surveyId: number,
    status: SurveyStatus,
  ): Observable<void> {
    return this._api.updateSurveyStatus(surveyId, status);
  }

  public getById(surveyId: number): Observable<Survey> {
    return this._api.getById(surveyId);
  }

  public getHighCpiDetails(
    surveyId: number,
  ): Observable<HighCpiConfirmationDetails> {
    return this._api.getHighCpiDetails(surveyId);
  }

  public logHighCpiConfirmation(
    surveyId: number,
    cpi: number | string,
  ): Observable<void> {
    const log = new HighCpiConfirmationActivityGenerator(
      surveyId,
      cpi,
    ).generate();

    return this._api.logSurveyActivity(log);
  }

  public updateCurrentCpi(surveyId: number, cpi: number): Observable<Survey> {
    return this._api
      .updateCurrentCpi(surveyId, cpi)
      .pipe(switchMap(() => this._api.getById(surveyId)));
  }

  public patchSurvey(
    surveyId: number,
    payload: SurveyPatchPayloadDto[],
  ): Observable<void> {
    return this._api.patchSurvey(surveyId, payload);
  }

  public getSurveySupplierCpis(
    surveyId: number,
  ): Observable<SurveySupplierCpi[]> {
    return this._api.getSurveySupplierCpis(surveyId);
  }

  public getQuotas(surveyId: number, page?: number): Observable<QuotaPage> {
    const limit = page === undefined ? undefined : QUOTAS_PAGE_LIMIT;
    return this._api.getQuotas(surveyId, page, limit);
  }

  public setQuotaLock(
    surveyId: number,
    psQuotaId: string,
    locked: boolean,
  ): Observable<void> {
    return this._api.setQuotaLock(surveyId, psQuotaId, locked);
  }

  public setQuotaCurrentTarget(
    surveyId: number,
    psQuotaId: string,
    currentTarget: number,
    updateSupplier: boolean,
  ): Observable<SurveyQuotaUpdate> {
    return this._api.setQuotaCurrentTarget(
      surveyId,
      psQuotaId,
      currentTarget,
      updateSupplier,
    );
  }

  public launchTrafficChannel(
    surveyId: number,
    payload: LaunchTrafficChannelRequestDto,
    bulk = false,
  ): Observable<void> {
    return this._api.launchTrafficChannel(surveyId, payload, bulk);
  }

  public searchInvoicedSurveysWithBillingId(
    billingId: string,
  ): Observable<void> {
    return this._api.searchInvoicedSurveysWithBillingId(billingId);
  }

  public verifyBillingIdAgainstBillingRules(
    billingId: string,
    billingRuleRecord: BillingRuleRecord,
  ): Observable<void> {
    return this._api.verifyBillingIdAgainstBillingRules(
      billingId,
      billingRuleRecord,
    );
  }

  public groupSurveys(
    surveyIds: number[],
    groupTitle: string,
  ): Observable<void> {
    return this._api.groupSurveys(surveyIds, groupTitle);
  }

  public deleteSurvey(surveyId: number): Observable<void> {
    return this._api.deleteSurvey(surveyId);
  }
}
